import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import { AppDispatch, RootState } from "../store";
import { updateJvPartner, updatekeyProvisionData, updatePropertyManager } from "./updateDataSlice";
import { baseUrl } from "config/config";
import { getApiCall, postApiCall } from "../../services/apiHelper";

// Define the types for key provisions form fields
export interface KeyAttributes {
  firmName?: string;
  keyContacts?: string;
  email?: string;
  riskRating?: number;
}

export interface HoldingStructureAttributes {
  specialPurposeVehicle?: string;
  holdingCompany?: string;
}

export interface JvPartnerData {
  assetPlanId?: string;
  jvPartnerFirmName?: string;
  isPartnerDeveloper?: boolean;
  willWorkWithJvPartnerAgain?: boolean;
  keyContact?: string;
  email?: string;
  comments?: string;
  responsiveness?: number;
  reporting?: number;
  flexibility?: number;
  ethics?: number;
  communication?: number;
  collaboration?: number;
  rankComments?: string;
  isJvPartnerAlsoPrM?: boolean;
}

export interface PropertyManagerData {
  rfpProcess?: string;
  anotherProperty?: string;
  teamWorkingComments?: string;
  responsiveCommunication?: string;
  incidentManager?: string;
  innovationAndTech?: string;
  riskMitigation?: string;
  timelyReporting?: string;
  tenantRelations?: string;
  vendorSelection?: string;
  prmRankComments?: string;
}

export interface KeyProvisionsData {
  planningAdviser: KeyAttributes;
  buildingContractors: KeyAttributes;
  leasingAgents: KeyAttributes;
  insuranceCompanyDetails: KeyAttributes;
  holdingStructure: HoldingStructureAttributes;
  jvPartnerData: JvPartnerData[];
  propertyManagerData: PropertyManagerData;
  assetPlanId?: string;
}

interface InitialState {
  data: KeyProvisionsData;
  jvPartnerData: JvPartnerData[];
  propertyManagerData: PropertyManagerData;
  error: string;
  loading: boolean;
  jvPartnerLoading: boolean;
  propertyManagerLoading: boolean;
  cacheKey: string | undefined;
  prmCacheKey: string | undefined,
  jvPartnerCacheKey: string | undefined,
  isNoData: boolean;
  isDirty: boolean; // Track if form has unsaved changes
}

// Initial state for the form
const initialState: InitialState = {
  data: {
    planningAdviser: {
      firmName: "",
      keyContacts: "",
      email: "",
      riskRating: undefined,
    },
    buildingContractors: {
      firmName: "",
      keyContacts: "",
      email: "",
      riskRating: undefined,
    },
    leasingAgents: {
      firmName: "",
      keyContacts: "",
      email: "",
      riskRating: undefined,
    },
    insuranceCompanyDetails: {
      firmName: "",
      keyContacts: "",
      email: "",
      riskRating: undefined,
    },
    holdingStructure: {
      specialPurposeVehicle: "",
      holdingCompany: "",
    },
    jvPartnerData: [],
    propertyManagerData: {
      rfpProcess: "",
      anotherProperty: "",
      teamWorkingComments: "",
      responsiveCommunication: "",
      incidentManager: "",
      innovationAndTech: "",
      riskMitigation: "",
      timelyReporting: "",
      tenantRelations: "",
      vendorSelection: "",
      prmRankComments: ""
    },
  },
  jvPartnerData: [],
  propertyManagerData: {
    rfpProcess: "",
    anotherProperty: "",
    teamWorkingComments: "",
    responsiveCommunication: "",
    incidentManager: "",
    innovationAndTech: "",
    riskMitigation: "",
    timelyReporting: "",
    tenantRelations: "",
    vendorSelection: "",
    prmRankComments: ""
  },
  error: '',
  loading: false,
  jvPartnerLoading: false,
  propertyManagerLoading: false,
  cacheKey: undefined,
  prmCacheKey:undefined,
  jvPartnerCacheKey:undefined,
  isNoData: false,
  isDirty: false,
};

// Create the slice
const keyProvisionEditFormSlice = createSlice({
  name: "keyProvisionEditForm",
  initialState,
  reducers: {
    resetState: (state) => {
      return initialState;
    },
    updateJvPartnerData: (state, action: PayloadAction<{ index: number; field: string; value: any }>) => {
      const { index, field, value } = action.payload;
      if (!state.jvPartnerData[index]) {
        state.jvPartnerData[index] = {};
      }
      state.jvPartnerData[index] = {
        ...state.jvPartnerData[index],
        [field]: value
      };
    },
    updatePropertyManagerData: (state, action: PayloadAction<{ field: string; value: any }>) => {
      const { field, value } = action.payload;
      state.propertyManagerData = {
        ...state.propertyManagerData,
        [field]: value
      };
    },
  },
  extraReducers: (builder) => {
    builder.addCase(getKeyProvisions.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(getKeyProvisions.fulfilled, (state, action: PayloadAction<KeyProvisionsData>) => {
      state.loading = false;
      state.data = action.payload;
      state.cacheKey = 'keyProvisionsCache';
      state.isNoData = Object.keys(action.payload).length === 0;
    });
    builder.addCase(getKeyProvisions.rejected, (state, action) => {
      state.loading = false;
      state.error = action.error.message || 'Something went wrong';
    });

    builder.addCase(getJvPartnerData.pending, (state) => {
      state.jvPartnerLoading = true;
    });
    builder.addCase(getJvPartnerData.fulfilled, (state, action: PayloadAction<JvPartnerData[]>) => {
      state.jvPartnerLoading = false;
      state.jvPartnerData = action.payload;
      state.jvPartnerCacheKey = 'keyProvisionsJVCache';
    });
    builder.addCase(getJvPartnerData.rejected, (state, action) => {
      state.jvPartnerLoading = false;
      state.error = action.error.message || 'Failed to fetch JV Partner data';
    });

    builder.addCase(getPropertyManagerData.pending, (state) => {
      state.propertyManagerLoading = true;
    });
    builder.addCase(getPropertyManagerData.fulfilled, (state, action: PayloadAction<PropertyManagerData>) => {
      state.propertyManagerLoading = false;
      state.propertyManagerData = action.payload;
      state.prmCacheKey = 'keyProvisionsPrmCache';
    });
    builder.addCase(getPropertyManagerData.rejected, (state, action) => {
      state.propertyManagerLoading = false;
      state.error = action.error.message || 'Failed to fetch Property Manager data';
    });
  }

});

// Create async thunk for fetching key provisions data
export const getKeyProvisions = createAsyncThunk<
  KeyProvisionsData,
  any,
  { state: RootState; dispatch: AppDispatch }
>("getKeyProvisions", async (payload: any, { getState, rejectWithValue, dispatch }) => {
  const state = getState().keyProvisionEditForm;

  if (state.cacheKey === 'keyProvisionsCache') {
    // Return the current data if it exists in the state and cacheKey is set
    return state.data;
  }

  try {
    const response: any = await postApiCall(`${baseUrl}KeyProvisions/GetKeyProvisionsByAssetId?AssetId=${payload}`, payload);
    console.log('API response for key provisions:', response);

    // Get the response data or use initial state if empty
    const responseData = response;
    responseData.holdingStructure = responseData.holdingStructure || { specialPurposeVehicle: "", holdingCompany: "" };
    responseData.planningAdviser = responseData.planningAdviser || { firmName: "", keyContacts: "", email: "", riskRating: undefined };
    responseData.buildingContractors = responseData.buildingContractors || { firmName: "", keyContacts: "", email: "", riskRating: undefined };
    responseData.leasingAgents = responseData.leasingAgents || { firmName: "", keyContacts: "", email: "", riskRating: undefined };
    responseData.insuranceCompanyDetails = responseData.insuranceCompanyDetails || { firmName: "", keyContacts: "", email: "", riskRating: undefined };
    // Update the updateDataSlice with the fetched data
    dispatch(updatekeyProvisionData(responseData));

    return responseData;
  } catch (error) {
    console.error('Error fetching key provisions:', error);
    return rejectWithValue('Failed to fetch key provisions data');
  }
});

// Create async thunk for fetching JV Partner data
export const getJvPartnerData = createAsyncThunk<
  JvPartnerData[],
  any,
  { state: RootState; dispatch: AppDispatch }
>("getJvPartnerData", async (payload: any, { getState, rejectWithValue, dispatch }) => {
  const Ownershipstate: any = getState().summary?.dapData?.ownershipType;
  const state: any = getState().keyProvisionEditForm;
  if (state.jvPartnerCacheKey === 'keyProvisionsJVCache') {
    return state.jvPartnerData;
    
  }
  try {
    const response: any = await postApiCall(`${baseUrl}KeyProvisions/GetJVsByAssetId?AssetId=${payload}`, payload);
    const responseData =  ((response.length === 0) && (Ownershipstate === "Unconsolidated JV")) ? [{
      jvPartnerFirmName: "",
      isPartnerDeveloper: null,
      willWorkWithJvPartnerAgain: null,
      keyContact: "",
      email: "",
      comments: "",
      responsiveness: null,
      reporting: null,
      flexibility: null,
      ethics: null,
      communication: null,
      collaboration: null,
      rankComments: "",
      isJvPartnerAlsoPrM: null
    }] : response;
    dispatch(updateJvPartner(responseData))
    return responseData
  } catch (error) {
    console.error('Error fetching JV Partner data:', error);
    return rejectWithValue('Failed to fetch JV Partner data');
  }
});

// Create async thunk for fetching Property Manager data
export const getPropertyManagerData = createAsyncThunk<
  PropertyManagerData,
  any,
  { state: RootState; dispatch: AppDispatch }
>("getPropertyManagerData", async (payload: any, { getState, rejectWithValue, dispatch }) => {
  const state: any = getState().keyProvisionEditForm;

  if (state.prmCacheKey === 'keyProvisionsPrmCache') {
    return state.propertyManagerData;
  }

  try {
    const response: any = await postApiCall(`${baseUrl}KeyProvisions/GetKeyProvisionsPrmSection?AssetPlanId=${payload}`, payload);
    dispatch(updatePropertyManager(response))
    return response;
  } catch (error) {
    console.error('Error fetching Property Manager data:', error);
    return rejectWithValue('Failed to fetch Property Manager data');
  }
});

// Export the reducer
export const {
  resetState: resetKeyProvisionStore,
  updateJvPartnerData,
  updatePropertyManagerData
} = keyProvisionEditFormSlice.actions;
export default keyProvisionEditFormSlice.reducer;
function getState() {
  throw new Error("Function not implemented.");
}

